import React, { useState, useEffect } from "react";
import {
  Table,
  Button,
  Input,
  DatePicker,
  Space,
  Pagination,
  Card,
  Modal,
  Form,
  Tag,
  Select,
  Tooltip,
} from "antd";
import { CloseCircleFilled, PlusOutlined } from "@ant-design/icons";
import FileIcon from "../../assets/images/file-icon.svg";
import ViewTableBtn from "../../assets/images/view-icon.svg";
import { useParams, useNavigate } from 'react-router-dom';
import EditTableBtn from "../../assets/images/action-edit-btn.svg";
import DeleteTableBtn from "../../assets/images/action-delete-btn.svg";
import DeleteModalBtn from "../../assets/images/delete-icon-modal.svg";
import useRequest from "../../hooks/useRequest";
import Select01 from "react-select";
import { Severty, ShowToast } from "../../helper/toast";

// Permission helper
const getPermissionByName = (moduleName) => {
  try {
    const user = JSON.parse(localStorage.getItem("admin_userProfile") || "{}");
    if (user.type === "Admin") {
      return { view: true, add: true, edit: true, delete: true };
    }
    // Case/whitespace-insensitive match — see BlogTabs.js's identical fix.
    const normalizedTarget = String(moduleName || "").trim().toLowerCase();
    const permissions = user?.roleId?.permission || [];
    return (
      permissions.find(
        (p) => String(p?.name || "").trim().toLowerCase() === normalizedTarget
      ) || {}
    );
  } catch {
    return {};
  }
};

const { Option } = Select;
const colorBadges = {
  Red: "red",
  Yellow: "gold",
  Pink: "magenta",
  Blue: "blue",
  Green: "green",
  Black: "black",
  Purple: "purple",
  Gray: "gray",
  // Navy Blue: "geekblue",
};

// Sub themes are stored as a flat list; older records may hold a nested one.
const toSubPillars = (v) =>
  (Array.isArray(v) ? v : v ? [v] : [])
    .flat(Infinity)
    .map((t) => String(t).trim())
    .filter(Boolean);

const ManageContentTabs = () => {
  const navigate = useNavigate();
  const contentThemesPermission = getPermissionByName("Content Themes");
  
  // Redirect if user lacks view permission
  useEffect(() => {
    if (!contentThemesPermission?.view) {
      navigate("/dashboard");
    }
  }, [contentThemesPermission?.view]);
  
  const [data, setData] = useState([]);
  const [viewModalVisible, setViewModalVisible] = useState(false);
  const [addModalVisible, setAddModalVisible] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [selectedPillar, setSelectedPillar] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [selectedDate, setSelectedDate] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const { request } = useRequest();
  const [form] = Form.useForm();
  const { id } = useParams();
  
  const handleView = (record) => {
    setSelectedPillar(record);
    setViewModalVisible(true);
  };

  const handleEdit = (record) => {
    setSelectedPillar(record);
    console.log("record", record);
    form.setFieldsValue({
      name: record.name, // ⬅️ should be name, not pillar
      subTheme: toSubPillars(record.subPillars),
      color: record.color,
    });
    setEditModalVisible(true);
  };

  const handleDelete = (record) => {
    setSelectedPillar(record);
    setDeleteModalVisible(true);
  };

  const handleAddNew = () => {
    form.resetFields();
    setSelectedOptions([]);      // <-- Reset sub pillar selection
    setSelectedColor(null);      // <-- Reset color selection
    setAddModalVisible(true);
  };

  const columns = [
    {
      title: "COLOR BADGE",
      dataIndex: "color",
      key: "color",
      render: (color) => (
        <div className="color-badge-table">
          <div
            style={{
              width: 16,
              height: 16,
              backgroundColor: color,
              borderRadius: "50%",
              border: "1px solid #ccc",
            }}
          />
        </div>
      ),
    },
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      render: (name) => (
        <div className="color-badge-table">
          <img src={FileIcon} alt="icon" />
          {name}
        </div>
      ),
    },
     {
      title: "Sub Theme",
      dataIndex: "subPillars",
      key: "subPillars",
      render: (subPillars) => (
        <span>{Array.isArray(subPillars) ? subPillars.join(", ") : "-"}</span>
      ),
    },
    {
      title: "LAST UPDATE",
      dataIndex: "updated_at",
      key: "updated_at",
      render: (updated_at) => (
        <div className="color-badge-table">
          <span className="date-text">
            {new Date(updated_at).toLocaleDateString("en-GB")}
          </span>
        </div>
      ),
    },
    {
      title: "ACTION",
      key: "action",
      render: (_, record) => (
        <div className="action-icons">
          <Tooltip title="View">
            <Button
              className="table-light-fill-btn"
              onClick={() => handleView(record)}
            >
              <img src={ViewTableBtn} alt="View" />
            </Button>
          </Tooltip>
          {contentThemesPermission?.edit && (
            <Tooltip title="Edit">
              <Button
                className="table-primary-fill-btn"
                onClick={() => handleEdit(record)}
              >
                <img src={EditTableBtn} alt="Edit" />
              </Button>
            </Tooltip>
          )}
          {contentThemesPermission?.delete && (
            <Tooltip title="Delete">
              <Button
                className="table-danger-fill-btn"
                onClick={() => handleDelete(record)}
              >
                <img src={DeleteTableBtn} alt="Delete" />
              </Button>
            </Tooltip>
          )}
        </div>
      ),
    },
  ];

  const inputOptions = [
    { value: "design", label: "Design" },
    { value: "development", label: "Development" },
    { value: "testing", label: "Testing" },

  ];

  const customStyles = {
    control: (provided) => ({
      ...provided,
      border: "1px solid #ccc",
      boxShadow: "none",
      minHeight: "40px",
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected ? "#e6f7ff" : "#fff",
      color: "#000",
    }),
  };
  const [selectedOptions, setSelectedOptions] = useState([]);
  const handleChange = (selected) => {
    // Always ensure selectedOptions is an array
    setSelectedOptions(Array.isArray(selected) ? selected : []);
  };
  const removeOption = (valueToRemove) => {
    const filtered = selectedOptions.filter(
      (opt) => opt.value !== valueToRemove
    );
    setSelectedOptions(filtered);
  };
  const [selectedColor, setSelectedColor] = useState(null);

  const handleColorChange = (value) => {
    setSelectedColor(value);
  };

  const addTheme = (payload) => {
    request({
      url: "/admin/access-module/content/"+id,
      method: "POST",
      data: payload,
      onSuccess: (data) => {
        if (data.status === 200) {
          ShowToast("Theme added successfully", Severty.SUCCESS);
        } else {
          ShowToast(data.message, Severty.ERROR);
        }
      },
      onError: (error) => {
        ShowToast("Theme addition failed", Severty.ERROR);
      },
    });
    getThemes(); // 👈 refresh
    setAddModalVisible(false);
  };
  const updateTheme = (payload) => {
    if (!selectedPillar?._id) return;

    request({
      url: `/admin/access-module/content/${selectedPillar._id}`, // Assuming _id is the backend ID
      method: "PUT",
      data: payload,
      onSuccess: (data) => {
        if (data.status === 200) {
          ShowToast("Theme updated successfully", Severty.SUCCESS);
          setEditModalVisible(false);
          getThemes();
          // Optional: refresh data from API or update local state
        } else {
          ShowToast(data.message, Severty.ERROR);
        }
      },
      onError: () => {
        ShowToast("Theme update failed", Severty.ERROR);
      },
    });
  };

  const getThemes = () => {
    let url = "/admin/access-module/content/"+id;
    const query = [];
    if (searchText) query.push(`name=${encodeURIComponent(searchText)}`);
    if (selectedDate) query.push(`date=${encodeURIComponent(selectedDate.format("YYYY-MM-DD"))}`);
    query.push(`page=${currentPage}`);
    query.push(`limit=${pageSize}`);
    if (query.length) url += "?" + query.join("&");

    request({
      url,
      method: "GET",
      onSuccess: (response) => {
        if (response) {
          setData(response.data.themes);
          setTotal(response.data.total || 0);
        } else {
          ShowToast(
            response.message || "Failed to fetch themes",
            Severty.ERROR
          );
        }
      },
      onError: (error) => {
        console.error("Error fetching themes:", error);
        ShowToast("Error fetching theme data", Severty.ERROR);
      },
    });
  };
  const handleDeletePillar = () => {
    if (!selectedPillar?._id) return;

    request({
      url: `/admin/access-module/content/${selectedPillar._id}`, // ✅ API ka URL
      method: "DELETE",
      onSuccess: (res) => {
        ShowToast("Pillar deleted successfully", Severty.SUCCESS);
        getThemes(); // ✅ Refresh list
        setDeleteModalVisible(false);
      },
      onError: () => {
        ShowToast("Failed to delete pillar", Severty.ERROR);
      },
    });
  };

  useEffect(() => {
    getThemes();
    // eslint-disable-next-line
  }, [searchText, selectedDate, currentPage, pageSize]);
  return (
    <div className="driver-management-container">
      <Card
        title="Content Theme"
        bordered={false}
        className="criclebox tablespace mb-24"
        extra={
          <div className="d-flex align-items-end gap-3 flex-wrap">
            {contentThemesPermission?.view && (
              <div className="pageHeadingSearch">
                <Input.Search
                  placeholder="Search By Name"
                  className="searchInput"
                  allowClear
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                />
              </div>
            )}
            {contentThemesPermission?.view && (
              <div className="role-wrap">
                <DatePicker
                  placeholder="Date"
                  value={selectedDate}
                  onChange={setSelectedDate}
                  allowClear
                />
              </div>
            )}
                    {contentThemesPermission?.add && (
          <div className="role-wrap">
            <Button
              className="add-table-modal"
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAddNew}
            >
              Add Content Theme
            </Button>
          </div>
        )}
          </div>
        }
      >
        <Table
          columns={columns}
          dataSource={data}
          pagination={false}
          className="table-responsive"
        />
        <div style={{ marginTop: 16, textAlign: "center" }}>
          <Pagination
            current={currentPage}
            pageSize={pageSize}
            total={total}
            showSizeChanger
            onChange={(page, size) => {
              setCurrentPage(page);
              setPageSize(size);
            }}
          />
        </div>
      </Card>

      {/* View Modal */}
      <Modal
        width={757}
        className="table_modal"
        title="Content Theme Details"
        visible={viewModalVisible}
        onCancel={() => setViewModalVisible(false)}
        footer={null}
      >
        <div className="piller-view-detail">
          <div className="pill-detail-list">
            <h6>Badge:</h6>
            <span
              className="color-tag"
              style={{
                backgroundColor: selectedPillar?.color || "#999",
                width: "30px",
                height: "30px",
                display: "inline-block",
                borderRadius: "50%",
              }}
            />
          </div>

          <div className="pill-detail-list">
            <h6>Theme Name:</h6>
            <span>{selectedPillar?.name || "N/A"}</span>
          </div>

          <div className="pill-detail-list">
            <h6>Sub Theme Name:</h6>
            <div className="subpillar-tags">
              {Array.isArray(selectedPillar?.subPillars) && selectedPillar.subPillars.length > 0 ? (
                selectedPillar.subPillars.map((item, idx) => (
                  <div className="tag-subpillar" key={idx}>
                    {item}
                  </div>
                ))
              ) : (
                <span>No Sub Pillars</span>
              )}
            </div>
          </div>
        </div>
      </Modal>


      {/* Add Modal */}
      <Modal
        width={757}
        className="table_modal"
        title="Add Content Theme"
        visible={addModalVisible}
        footer={false}
        onCancel={() => setAddModalVisible(false)}
        onOk={() => {
          addTheme({
            name: form.getFieldValue("name"),
            subPillars: toSubPillars(form.getFieldValue("subTheme")),
            color: form.getFieldValue("color"),
          });
        }}
      >
         <Form form={form} layout="vertical">
          <Form.Item
            label="Theme Name"
            name="name"
            rules={[{ required: false, message: "Please enter theme name" }]}
          >
            <Input 
              placeholder="Enter theme name" 
              disabled={!contentThemesPermission?.add}
            />
          </Form.Item>
          <Form.Item
            label="Sub theme Name"
            name="subTheme"
            rules={[{ required: false, message: "Please enter sub theme name" }]}
          >
            <Select
              mode="tags"
              style={{ width: "100%" }}
              tokenSeparators={[","]}
              placeholder="Type a sub theme and press Enter — add as many as you need"
              disabled={!contentThemesPermission?.add}
            />
          </Form.Item>

          <Form.Item
            className="color-select-bar"
            label="Color"
            name="color"
            rules={[{ required: false, message: "Please select color" }]}
          >
            <Input 
              type="color" 
              placeholder="choose a color" 
              disabled={!contentThemesPermission?.add}
            />
          </Form.Item>
        </Form>
        <div className="action-footer-buttons">
          <Button className="schedule-btn" onClick={() => setAddModalVisible(false)}>
            Cancel
          </Button>
          {contentThemesPermission?.add && (
            <Button
              className="submitted-btn"
              onClick={() => {
                form.validateFields().then((values) => {
                  addTheme({
                    name: values.name,
                    subPillars: toSubPillars(values.subTheme),
                    color: values.color,
                  });
                });
              }}
            >
              Save changes
            </Button>
          )}
        </div>
      </Modal>

      {/* Edit Modal */}
      <Modal
        className="table_modal"
        title="Edit Content Theme"
        width={757}
        footer={false}
        visible={editModalVisible}
        onCancel={() => setEditModalVisible(false)}
        onOk={() => {
          form.validateFields().then((values) => {
            const updated = data.map((item) =>
              item.key === selectedPillar.key
                ? { ...item, pillar: values.name, color: values.color }
                : item
            );
            setData(updated);
            setEditModalVisible(false);
          });
        }}
      >
         <Form form={form} layout="vertical">
          <Form.Item
            label="Theme Name"
            name="name"
            rules={[{ required: true, message: "Please enter theme name" }]}
          >
            <Input 
              placeholder="Enter theme name" 
              disabled={!contentThemesPermission?.edit}
            />
          </Form.Item>
          <Form.Item
            label="Sub theme Name"
            name="subTheme"
            rules={[{ required: true, message: "Please enter theme name" }]}
          >
            <Select
              mode="tags"
              style={{ width: "100%" }}
              tokenSeparators={[","]}
              placeholder="Type a sub theme and press Enter — add as many as you need"
              disabled={!contentThemesPermission?.edit}
            />
          </Form.Item>

          <Form.Item
            className="color-select-bar"
            label="Color"
            name="color"
            rules={[{ required: true, message: "Please select color" }]}
          >
            <Input 
              type="color" 
              placeholder="choose a color" 
              disabled={!contentThemesPermission?.edit}
            />
          </Form.Item>
        </Form>
        <div className="action-footer-buttons">
          <Button className="schedule-btn" onClick={() => setEditModalVisible(false)}>Cancel</Button>
          {contentThemesPermission?.edit && (
            <Button
              className="submitted-btn"
              onClick={() => {
                form.validateFields().then((values) => {
                  updateTheme({
                    name: values.name,
                    subPillars: toSubPillars(values.subTheme),
                    color: values.color,
                  });
                });
              }}
            >
              Save
            </Button>
          )}
        </div>
      </Modal>

      {/* Delete Modal */}
      <Modal
        className="table_modal delete-modal"
        footer={false}
        title={false}
        width={450}
        visible={deleteModalVisible}
        onCancel={() => setDeleteModalVisible(false)}
        onOk={() => {
          setData(data.filter((item) => item.key !== selectedPillar.key));
          setDeleteModalVisible(false);
        }}
        okText="Yes, confirm"
        cancelButtonProps={{ style: { display: "none" } }}
      >
        <div className="delete-modal-body">
          <img src={DeleteModalBtn} alt="delete" />
          <h3>Are you sure?</h3>
          <p> Are you sure you want to delete this Theme ? </p>
          {contentThemesPermission?.delete && (
            <Button className="delete-fill-btn" onClick={handleDeletePillar} >Yes, confirm</Button>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default ManageContentTabs;
